<template>
    <AppModal ref="modal" title="Згенерувати контент за допомогою ШІ" @on-hide="hide" size="full">
        <div class="modal-body">
            <FieldSelectionStep
                v-if="step === 'fieldSelectionStep'"
                ref="fieldSelectionStep"
                :product="product"
                :main-integration-language="mainIntegrationLanguage"
                v-model:filters-who-need-exclude="filtersWhoNeedExclude"
                :generation-fields="fieldsForGenerate"
                @update:generation-fields="selectGenerationFields"
            />
            <NameGenerationStep
                v-else-if="step === 'nameGenerationStep'"
                v-model:generated-name="generatedContent.names[mainIntegrationLanguage]"
                :product="product"
                :integration-service="integrationService"
                :main-integration-language="mainIntegrationLanguage"
                :geminiService="geminiService"
                :formatted-filters="formattedFiltersForCreatePrompt"
                :project-uuid="projectUuid"
                :tokenBalance="tokenBalance"
                @generating-started="generatingStarted = true"
                @generating-stopped="generatingStarted = false"
                @tokens-flushed="refreshBalance"
            />
            <KeywordGenerationStep
                v-else-if="step === 'keywordGenerationStep'"
                v-model:generated-seo="generatedContent.seos[mainIntegrationLanguage]"
                :product="product"
                :names="nameForProcessing"
                :integration-service="integrationService"
                :main-integration-language="mainIntegrationLanguage"
                :geminiService="geminiService"
                :formatted-filters="formattedFiltersForCreatePrompt"
                :project-uuid="projectUuid"
                :tokenBalance="tokenBalance"
                @generating-started="generatingStarted = true"
                @generating-stopped="generatingStarted = false;"
                @tokens-flushed="refreshBalance"
            />
            <DescriptionGenerationStep
                v-else-if="step === 'descriptionGenerationStep'"
                v-model:generated-description="generatedContent.descriptions[mainIntegrationLanguage]"
                :product="product"
                :names="nameForProcessing"
                :seos="seoForProcessing"
                :integration-service="integrationService"
                :main-integration-language="mainIntegrationLanguage"
                :geminiService="geminiService"
                :formatted-filters="formattedFiltersForCreatePrompt"
                :project-uuid="projectUuid"
                :tokenBalance="tokenBalance"
                @generating-started="generatingStarted = true"
                @generating-stopped="generatingStarted = false"
                @tokens-flushed="refreshBalance"
            />
            <TranslationGenerationStep
                v-else-if="step === 'finallyReviewStep'"
                v-model:generated-content="generatedContent"
                :product="product"
                :main-integration-language="mainIntegrationLanguage"
                :geminiService="geminiService"
                :integrationLanguages="integrationLanguages"
                :project-uuid="projectUuid"
                :tokenBalance="tokenBalance"
                @generating-started="generatingStarted = true"
                @generating-stopped="generatingStarted = false"
                @tokens-flushed="refreshBalance"
            />
            <Alert v-if="tokenBalance !== null && tokenBalance <= 0" type="warning" class="mt-3 mb-0">
                {{ $t('alerts.insufficient_ai_balance') }}
            </Alert>
        </div>
        <div class="modal-footer" v-if="geminiService && !generatingStarted">
            <AppButton color="indigo" @click="saveChanges" v-if="step !== 'fieldSelectionStep'">
                {{ $t('buttons.save_changes') }}
            </AppButton>
            <AppButton
                v-if="step !== 'finallyReviewStep'"
                color="indigo"
                :disabled="tokenBalance !== null && tokenBalance <= 0"
                @click="setNextStep(step)"
            >
                {{ $t('buttons.continue') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import Alert from '../../../../components/AlertComponent.vue';
import FieldSelectionStep from './modalSteps/FieldSelectionStep.vue';
import NameGenerationStep from './modalSteps/NameGenerationStep.vue';
import KeywordGenerationStep from './modalSteps/KeywordGenerationStep.vue';
import DescriptionGenerationStep from './modalSteps/DescriptionGenerationStep.vue';
import TranslationGenerationStep from './modalSteps/TranslationGenerationStep.vue';

import { httpClient } from '../../../../services/HttpClient';
import GeminiService from '../../../../services/GeminiService/GeminiService';

export default {
    name: 'GenerateContentWithAi',
    components: {
        AppModal, FieldSelectionStep, AppButton, Alert, NameGenerationStep, KeywordGenerationStep,
        DescriptionGenerationStep, TranslationGenerationStep
    },
    props: {
        product: {
            type: Object,
            required: true
        },
        integrationService: {
            type: String,
            default: ''
        },
        integrationLanguages: {
            type: Array,
            default: () => []
        },
        productFilters: {
            type: Array,
            default: () => []
        }
    },
    emits: ['contentGenerated'],
    data() {
        return {
            step: 'fieldSelectionStep',
            steps: [
                'fieldSelectionStep',
                'nameGenerationStep',
                'keywordGenerationStep',
                'descriptionGenerationStep',
                'finallyReviewStep'
            ],
            geminiService: null,
            generatingStarted: false,
            tokenBalance: null,
            generatedContent: {
                names: {},
                seos: {},
                descriptions: {}
            },
            filtersWhoNeedExclude: [],
            fieldsForGenerate: []
        }
    },
    methods: {
        selectGenerationFields(fields) {
            let newFields = fields,
                fieldKeys = fields.map(field => field.key);

            if (!fieldKeys.includes('nameGenerationStep') && !Object.keys(this.product.names).length) {
                newFields = [];
            }

            if ((!fieldKeys.includes('nameGenerationStep') && !Object.keys(this.product.names).length) &&
                (!fieldKeys.includes('keywordGenerationStep') && !Object.keys(this.product.seos).length)) {
                newFields = newFields.filter(field => field.key !== 'descriptionGenerationStep');
            }

            this.fieldsForGenerate = newFields;
        },
        async show() {
            this.setGeminiService();
            await this.fetchBalance();
            this.filtersWhoNeedExclude = this.productFilters
                .filter(filter => filter.ai_use_in_product_description === 0)
                .map(filter => ({ key: filter.uuid, value: filter.names['uk' || this.integrationLanguages[0]]}));

            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.step = 'fieldSelectionStep';
            this.generatedContent = {
                names: {},
                seos: {},
                descriptions: {}
            };
            this.fieldsForGenerate = [];
            this.filtersWhoNeedExclude = [];
            this.generatingStarted = false;
            this.geminiService = null;
            this.tokenBalance = null;
        },
        async fetchBalance() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`).then((response) => {
                this.tokenBalance = response.data.balance;
            })
        },
        setGeminiService() {
            this.geminiService = new GeminiService();
        },
        setNextStep(currentStep) {
            this.generatingStarted = true;
            const nextStep = this.whatIsNextStep(currentStep);

            this.step = nextStep;
        },
        whatIsNextStep(currentStep) {
            if (!this.fieldsForGenerate.length) {
                const currentIndex = this.steps.indexOf(currentStep);
                const residualSteps = this.steps.slice(currentIndex + 1);

                switch (residualSteps[0]) {
                    case 'nameGenerationStep':
                        return 'nameGenerationStep';
                    case 'keywordGenerationStep':
                        return 'keywordGenerationStep';
                    case 'descriptionGenerationStep':
                        return 'descriptionGenerationStep';
                    default:
                        return 'finallyReviewStep';
                }
            } else {
                let steps = [...this.steps];
                let clippedSteps = steps.splice(this.steps.indexOf(this.step) + 1);

                for (let step of clippedSteps) {
                    if (this.fieldsForGenerate.some(item => item.key === step)) {
                        return step;
                    }
                }

                return 'finallyReviewStep';
            }
        },
        async saveChanges() {
            let request = {};

            for (let [field, content] of Object.entries(this.generatedContent)) {
                if (Object.keys(content).length) {
                    request[field] = content;
                }
            }

            await httpClient.patch(`/integrations/products/${this.product.uuid}`, request).then((response) => {
                this.$emit('contentGenerated', response.data.data);
                this.hide();
            })
        },
        async refreshBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        }
    },
    computed: {
        mainIntegrationLanguage() {
            return this.integrationLanguages[0] || 'en';
        },
        nameForProcessing() {
            return Object.keys(this.generatedContent.names).length
                ? this.generatedContent.names[this.mainIntegrationLanguage]
                : this.product.names[this.mainIntegrationLanguage] || Object.values(this.product.names)[0] || null;
        },
        seoForProcessing() {
            return Object.keys(this.generatedContent.seos).length
                ? this.generatedContent.seos[this.mainIntegrationLanguage]
                : this.product.seos[this.mainIntegrationLanguage] || Object.values(this.product.seos)[0] || null;
        },
        formattedFiltersForCreatePrompt() {
            let formattedFilters = [];
            let filtersAfterFiltering = this.product.filters.filter(filter => {
                return !this.filtersWhoNeedExclude.some((filterForExclude) => filterForExclude.key === filter.uuid);
            })

            for (let filter of filtersAfterFiltering) {
                let formattedFilter = `${filter.names[this.mainIntegrationLanguage]} - ${filter.item.names[this.mainIntegrationLanguage]}`;

                formattedFilters.push(formattedFilter);
            }

            return formattedFilters;
        }
    }
}
</script>
<style>
.dots {
    animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
    0%, 20% {
        color: transparent;
        text-shadow: .25em 0 0 transparent, .5em 0 0 transparent;
    }
    40% {
        color: currentColor;
        text-shadow: .25em 0 0 transparent, .5em 0 0 transparent;
    }
    60% {
        text-shadow: .25em 0 0 currentColor, .5em 0 0 transparent;
    }
    80%, 100% {
        text-shadow: .25em 0 0 currentColor, .5em 0 0 currentColor;
    }
}
</style>
